import React, { useEffect } from 'react';
import { Box, Typography, LinearProgress, Rating } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getFeedbacksAction } from '../redux/actions/feedback';
import moment from 'moment';
import strings from '../i18n/language';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const ColHeader = ({ children, style }) => (
  <th style={{
    textAlign: 'left', fontSize: 11, fontWeight: 600, color: MUTED,
    textTransform: 'uppercase', letterSpacing: 0.6, padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`, ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '13px 16px', fontSize: 13, color: 'rgba(232,240,254,0.75)',
    borderBottom: `1px solid rgba(255,255,255,0.04)`, verticalAlign: 'middle', ...style,
  }}>
    {children}
  </td>
);

const NoteColor = (note) => {
  if (note >= 4) return '#5bbfb5';
  if (note >= 3) return '#ffc107';
  return '#e05c5c';
};

const Feedbacks = () => {
  const feedbacks = useSelector((state) => state.feedback.feedbacks);
  const isLoadingFeedbacks = useSelector((state) => state.feedback.isLoadingFeedbacks);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getFeedbacksAction(selectedCompany.id));
  }, [dispatch, selectedCompany]);

  useEffect(() => {
    if (!feedbacks) dispatch(getFeedbacksAction(selectedCompany.id));
  }, [feedbacks, dispatch, selectedCompany]);

  const avg = feedbacks?.length
    ? (feedbacks.reduce((sum, f) => sum + Number(f.note), 0) / feedbacks.length).toFixed(1)
    : null;

  return (
    <Box sx={{ width: "100%" }}>
      <div style={{ background: GLASS, border: `1px solid ${BORDER}`, borderRadius: 20, backdropFilter: 'blur(12px)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div>
            <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
              Opiniones
            </Typography>
            {feedbacks && feedbacks.length > 0 && (
              <Typography style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                {feedbacks.length} {feedbacks.length === 1 ? 'opinión' : 'opiniones'}
              </Typography>
            )}
          </div>
          {avg && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Rating
                value={parseFloat(avg)}
                readOnly
                precision={0.1}
                icon={<StarRoundedIcon style={{ fontSize: 18, color: '#ffc107' }} />}
                emptyIcon={<StarBorderRoundedIcon style={{ fontSize: 18, color: 'rgba(255,255,255,0.15)' }} />}
              />
              <span style={{ fontSize: 14, fontWeight: 700, color: NoteColor(parseFloat(avg)) }}>
                {avg}
              </span>
            </div>
          )}
        </div>

        {isLoadingFeedbacks && (
          <LinearProgress sx={{ height: 2, '& .MuiLinearProgress-bar': { background: BRAND } }} />
        )}

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ColHeader>{strings.LABEL_FEEDBACKS_HEADER_USER}</ColHeader>
                <ColHeader style={{ width: 140 }}>{strings.LABEL_FEEDBACKS_HEADER_NOTE}</ColHeader>
                <ColHeader>{strings.LABEL_FEEDBACKS_HEADER_MESSAGE}</ColHeader>
                <ColHeader style={{ width: 130 }}>{strings.LABEL_FEEDBACKS_HEADER_DATE}</ColHeader>
              </tr>
            </thead>
            <tbody>
              {feedbacks && feedbacks.length === 0 && !isLoadingFeedbacks && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: 48 }}>
                    <ForumRoundedIcon style={{ fontSize: 32, color: MUTED, display: 'block', margin: '0 auto 10px' }} />
                    <span style={{ fontSize: 13, color: MUTED }}>{strings.LABEL_NO_RESULTS}</span>
                  </td>
                </tr>
              )}
              {feedbacks && feedbacks.map((feedback) => (
                <tr key={feedback.id}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  style={{ transition: 'background 0.1s' }}
                >
                  <Td style={{ color: '#e8f0fe', fontWeight: 500 }}>{feedback.User.displayName}</Td>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Rating
                        value={feedback.note}
                        readOnly
                        icon={<StarRoundedIcon style={{ fontSize: 16, color: NoteColor(feedback.note) }} />}
                        emptyIcon={<StarBorderRoundedIcon style={{ fontSize: 16, color: 'rgba(255,255,255,0.15)' }} />}
                      />
                      <span style={{ fontSize: 12, fontWeight: 600, color: NoteColor(feedback.note) }}>
                        {feedback.note}
                      </span>
                    </div>
                  </Td>
                  <Td style={{ color: MUTED }}>{feedback.message || '—'}</Td>
                  <Td style={{ color: MUTED, fontSize: 12, whiteSpace: 'nowrap' }}>
                    {moment(feedback.date).format('LL')}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Box>
  );
};

export default Feedbacks;
